/** @jsxImportSource @emotion/react */

import styled from "@emotion/styled";
import { useEffect, Fragment } from "react";

import DeleteIcon from "@material-ui/icons/Delete";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import PerfectScrollbar from "react-perfect-scrollbar";

import axios from "axios";

import { useStoreon } from "storeon/react";
import Link from "@material-ui/core/Link";
import Button from "@material-ui/core/Button";

import confirm from "components/Dialog";
import t from "translations";

const EditorsListWrap = styled.ul`
  margin: 0px;

  > li {
    padding: 5px;
    cursor: pointer;
    color: #eee;
    margin: 0px;
    position: relative;
    border-bottom: 1px solid #595959;
    &.active {
      background: #555;
    }
    > div > a {
      margin-left: 5px;
    }
  }
`;
const B = styled.span`
  font-weight: 700;
`;

const DeleteButton = styled(DeleteIcon)`
  transition: opacity 0.2s ease-in-out;
  position: absolute;
  right: -3px;
  bottom: 0px;
  padding: 4px;
  color: white;
  height: 30px;
  width: 30px;
  opacity: 0.5;
  :hover {
    opacity: 1;
  }
`;

const EditorItem = props => {
  const { active, onClick, editor, onRemoveClick } = props;
  const { settings, title, id, canvasData, file } = editor;
  let canvas = {};
  let error = "";
  try {
    canvas = JSON.parse(atob(canvasData));
  } catch (err) {
    error = "Invalid canvas data";
    return null;
  }
  const W = canvas.cutW * 0.1;
  const H = canvas.cutH * 0.1;
  const hanldeLinkClick = e => {
    e.stopPropagation();
  };
  return (
    <li
      className={active ? "active" : ""}
      onClick={() => {
        onClick(editor);
      }}
    >
      <DeleteButton onClick={e => onRemoveClick(e, editor)} />
      <B>{title ? title : "No title"}</B> ID:{id} {active && "<- Editing now"}
      <br />
      Size: <B>{`${W.toFixed(1)}x${H.toFixed(1)}cm`}</B>
      {settings.isDynamicSize ? " (dynamic)" : " (static)"}
      {file && (
        <Fragment>
          <br /> Printfile:
          <Link onClick={hanldeLinkClick} target="_blank" href={file}>
            PDF
          </Link>
          <b>
            {" - " +
              file
                .split("_")
                .slice(1, -1)
                .join(" ")}
          </b>
        </Fragment>
      )}
      {error && error}
    </li>
  );
};

const PrintplateList = () => {
  const { dispatch, canvas, id, printplates } = useStoreon(
    "canvas",
    "id",
    "printplates"
  );
  useEffect(
    _ => {
      axios
        .get("/wp-json/dpe/v1/printplate")
        .then(resp => {
          if (resp.data.length > 0) {
            dispatch("printplates/set", resp.data);
          }
        })
        .catch(err => {
          console.error("Printplates list error", err);
        });
    },
    [dispatch]
  );

  useEffect(
    _ => {
      axios
        .get("/wp-json/dpe/v1/order", { params: { id } })
        .then(resp => {
          if (resp.data.length > 0) {
            dispatch("orders/set", resp.data);
          }
        })
        .catch(err => {
          console.error("Orders list error", err);
        });
    },
    [id, dispatch]
  );

  const handleEditorClick = editor => {
    dispatch("setEditorType/printplate");
    dispatch("id/set", editor.id);
    canvas.DPE.persistent.loadFromBase64(editor.canvasData);
    canvas.DPE.printplate.enable();
  };

  const handleDeleteClick = async (e, editor) => {
    e.stopPropagation();
    const result = await confirm.show({
      icon: <DeleteForeverIcon style={{ marginRight: 10 }} />,
      title: t.removeEditor
    });
    if (!result) {
      return;
    }

    const { title, id } = editor;
    axios
      .delete("/wp-json/dpe/v1/printplate", { data: { id } })
      .then(resp => {
        if (resp.data.id && resp.data.printplates) {
          dispatch("notification/info", `Printplate ${title} ID:${id} removed`);
          dispatch("printplates/set", resp.data.printplates);
          dispatch("id/set", null);
          canvas.clear();
          canvas.DPE.cutZone.initBounds();
          canvas.DPE.cutZone.initClipZone();
          canvas.requestRenderAll();
          canvas.DPE.cutZone.fitCutZone();
        } else {
          dispatch("notification/error", resp.data);
        }
      })
      .catch(err => {
        console.error("Printplate remove error", err);
        dispatch("notification/error", err.message);
      });
  };

  const handleNewClick = () => {
    dispatch("canvas/new/printplate");
  };

  if (!printplates) {
    return;
  }

  return (
    <Fragment>
      <Button variant="contained" onClick={handleNewClick}>
        New
      </Button>
      <PerfectScrollbar style={{ height: "calc(100% - 40px)" }}>
        <EditorsListWrap>
          {printplates.map(editor => {
            return (
              <EditorItem
                dispatch={dispatch}
                editor={editor}
                key={editor.id}
                active={id === editor.id}
                onClick={handleEditorClick}
                onRemoveClick={handleDeleteClick}
              />
            );
          })}
        </EditorsListWrap>
      </PerfectScrollbar>
    </Fragment>
  );
};

export default PrintplateList;
